import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import DatasetLinkedRoundedIcon from "@mui/icons-material/DatasetLinkedRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { MANUFACTURING_API, getErrorMessage, getRequest } from "./api";
import {
  EmptyStateCard,
  PageShell,
  SectionCard,
  SummaryCard,
  panelSx,
  statusChip,
} from "./ManufacturingCommon";

const formatCount = (value) => Number(value || 0).toLocaleString("en-IN");

function ManufacturingDashboardSkeleton() {
  const summarySkeletons = Array.from({ length: 4 });
  const sectionSkeletons = Array.from({ length: 4 });
  const rowSkeletons = Array.from({ length: 3 });

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr 1fr ",
            xl: "1fr 1fr 1fr 1fr",
          },
        }}
      >
        {summarySkeletons.map((_, index) => (
          <Box key={index} sx={{ ...panelSx, p: 2.2, minHeight: 210 }}>
            <Skeleton variant="text" width="42%" height={18} />
            <Skeleton variant="text" width="68%" height={28} sx={{ mt: 0.8 }} />
            <Skeleton variant="text" width="32%" height={46} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="88%" height={22} sx={{ mt: 1 }} />
            <Skeleton variant="rounded" width={118} height={34} sx={{ mt: 1.8, borderRadius: 2 }} />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            lg: "1fr 1fr",
          },
        }}
      >
        {sectionSkeletons.map((_, index) => (
          <Box key={index}>
            <Box sx={{ ...panelSx, p: 2.2 }}>
              <Skeleton variant="text" width="38%" height={30} />
              <Skeleton variant="text" width="72%" height={22} />
              <Stack spacing={1.25} sx={{ mt: 2 }}>
                {rowSkeletons.map((__, rowIndex) => (
                  <Box
                    key={rowIndex}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: "1px solid rgba(226,232,240,0.9)",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="52%" height={24} />
                        <Skeleton variant="text" width="76%" height={20} />
                      </Box>
                      <Skeleton variant="rounded" width={96} height={28} sx={{ borderRadius: 999 }} />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

export default function ManufacturingDashboard() {
  const navigate = useNavigate();
  const { activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({
    partners: [],
    products: [],
    orders: [],
  });

  const loadData = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const [partnersData, productsData, ordersData] = await Promise.all([
        getRequest(`${MANUFACTURING_API}/partners`, token, activeOrg),
        getRequest(`${MANUFACTURING_API}/products`, token, activeOrg),
        getRequest(`${MANUFACTURING_API}/orders`, token, activeOrg),
      ]);
      setSummary({
        partners: partnersData?.partners || [],
        products: productsData?.products || [],
        orders: ordersData?.orders || [],
      });
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load manufacturing dashboard"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const orderCounts = useMemo(
    () =>
      summary.orders.reduce((acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      }, {}),
    [summary.orders],
  );

  const totalUnitsPlanned = useMemo(
    () =>
      summary.orders.reduce(
        (acc, order) => acc + Number(order.target_quantity || 0),
        0,
      ),
    [summary.orders],
  );

  const totalUnitsConverted = useMemo(
    () =>
      summary.orders.reduce(
        (acc, order) => acc + Number(order.summary?.units_converted || 0),
        0,
      ),
    [summary.orders],
  );

  const releasedOrders = orderCounts.RELEASED || 0;
  const activeProductionOrders =
    (orderCounts.IN_PRODUCTION || 0) + (orderCounts.PARTIALLY_COMPLETED || 0);
  const recentOrders = [...summary.orders].slice(0, 6);
  const completionRate = totalUnitsPlanned
    ? Math.round((totalUnitsConverted / totalUnitsPlanned) * 100)
    : 0;

  return (
    <PageShell
      title="Manufacturing"
      subtitle="Run production planning, partner coordination, serial allocation, quality execution, and inventory conversion with the same operational polish as the rest of the platform."
      actions={
        <Stack spacing={1.25}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "rgba(226,232,240,0.72)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Factory Readiness
          </Typography>
          <Typography
            sx={{
              color: "#F8FAFC",
              fontSize: 26,
              fontWeight: 900,
              letterSpacing: "-0.04em",
            }}
          >
            {formatCount(totalUnitsPlanned)} units planned
          </Typography>
          <Typography
            sx={{
              color: "rgba(226,232,240,0.82)",
              fontSize: 14,
              lineHeight: 1.7,
            }}
          >
            {releasedOrders} released, {activeProductionOrders} actively moving
            through the line, {completionRate}% converted into inventory.
          </Typography>
        </Stack>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <ManufacturingDashboardSkeleton />
      ) : (
        <Stack spacing={3}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr 1fr ",
                xl: "1fr 1fr 1fr 1fr",
              },
            }}
          >
            <SummaryCard
              eyebrow="Partner Network"
              title="Approved manufacturing partners"
              value={formatCount(summary.partners.length)}
              subtitle="Qualified EMS, assembly, and factory partners available for production release."
              actionLabel="Open partners"
              onAction={() => navigate("/manufacturing/partners")}
            />

            <SummaryCard
              eyebrow="Product Definitions"
              title="Manufacturing products"
              value={formatCount(summary.products.length)}
              subtitle="Configured SKUs mapped to inventory asset types and logical device families."
              actionLabel="Open products"
              onAction={() => navigate("/manufacturing/products")}
            />

            <SummaryCard
              eyebrow="Production Queue"
              title="Open production orders"
              value={formatCount(summary.orders.length)}
              subtitle={`${releasedOrders} released and ${activeProductionOrders} already on the floor.`}
              actionLabel="Open orders"
              onAction={() => navigate("/manufacturing/orders")}
            />

            <SummaryCard
              eyebrow="Inventory Conversion"
              title="Units converted"
              value={formatCount(totalUnitsConverted)}
              subtitle={`${completionRate}% of the planned unit volume has already moved into inventory.`}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                lg: "1fr 1fr",
              },
            }}
          >
            {/* FULL WIDTH - Production Status Control */}
            <Box sx={{ gridColumn: { xs: "1 / -1", lg: "auto" } }}>
              <SectionCard
                title="Production Status Control"
                subtitle="Operational view of the current order pipeline and release posture."
              >
                {Object.keys(orderCounts).length ? (
                  <Stack spacing={1.25}>
                    {Object.entries(orderCounts)
                      .sort(([, left], [, right]) => right - left)
                      .map(([status, count]) => (
                        <Box
                          key={status}
                          sx={{
                            p: 1.4,
                            borderRadius: 3,
                            border: "1px solid rgba(226,232,240,0.9)",
                            backgroundColor: "#fff",
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                          >
                            {statusChip(status)}
                            <Typography
                              sx={{
                                fontSize: 24,
                                fontWeight: 900,
                                color: "#0F172A",
                                letterSpacing: "-0.03em",
                              }}
                            >
                              {formatCount(count)}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                  </Stack>
                ) : (
                  <EmptyStateCard
                    title="No production orders"
                    description="Create the first manufacturing order to start release planning and serial allocation."
                  />
                )}
              </SectionCard>
            </Box>

            {/* FULL WIDTH - Recent Orders */}
            <Box sx={{ gridColumn: { xs: "1 / -1", lg: "auto" } }}>
              <SectionCard
                title="Recent Orders"
                subtitle="Latest released and planned work packages across the active workspace."
                action={
                  <Button
                    variant="text"
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => navigate("/manufacturing/orders")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      color: "#F8FAFC",
                    }}
                  >
                    View all orders
                  </Button>
                }
              >
                {recentOrders.length ? (
                  <Stack spacing={1.25}>
                    {recentOrders.map((order) => (
                      <Box
                        key={order.id}
                        onClick={() =>
                          navigate(`/manufacturing/orders/${order.id}`)
                        }
                        sx={{
                          ...panelSx,
                          p: 1.75,
                          cursor: "pointer",
                          boxShadow: "none",
                          transition:
                            "transform 0.2s ease, box-shadow 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 14px 28px rgba(15,23,42,0.08)",
                          },
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                          spacing={1.5}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 18,
                                fontWeight: 800,
                                color: "#0F172A",
                              }}
                            >
                              {order.order_code}
                            </Typography>
                            <Typography
                              sx={{ mt: 0.5, color: "#64748B", fontSize: 13 }}
                            >
                              {order.partner?.name || "Partner pending"} •{" "}
                              {order.product?.name || "Product pending"} •
                              Target {formatCount(order.target_quantity)}
                            </Typography>
                          </Box>
                          <Stack
                            direction={{ xs: "row", md: "column" }} 
                            spacing={0.8}
                             alignItems={{ xs: "center", md: "center" }}
                          >
                            {statusChip(order.status)}

                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#64748B",
                              }}
                            >
                              Converted{" "}
                              {formatCount(order.summary?.units_converted || 0)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <EmptyStateCard
                    title="No recent orders"
                    description="Once production orders are created, the most recent work packages will appear here for quick review."
                  />
                )}
              </SectionCard>
            </Box>

            {/* HALF WIDTH - Factory Snapshot */}
            <Box>
              <SectionCard
                title="Factory Snapshot"
                subtitle="High-signal metrics your team can read in seconds."
              >
                <Stack spacing={1.25}>
                  {[
                    {
                      icon: (
                        <PrecisionManufacturingRoundedIcon
                          sx={{ color: "#1D4ED8" }}
                        />
                      ),
                      label: "Released orders",
                      value: releasedOrders,
                      helper: "Orders cleared for execution",
                    },
                    {
                      icon: (
                        <DatasetLinkedRoundedIcon sx={{ color: "#7C3AED" }} />
                      ),
                      label: "In production",
                      value: activeProductionOrders,
                      helper: "Orders with active line movement",
                    },
                    {
                      icon: <Inventory2RoundedIcon sx={{ color: "#0F766E" }} />,
                      label: "Converted assets",
                      value: totalUnitsConverted,
                      helper: "Units already converted into inventory",
                    },
                    {
                      icon: <BusinessRoundedIcon sx={{ color: "#C2410C" }} />,
                      label: "Partner coverage",
                      value: summary.partners.length,
                      helper: "Factories available for order assignment",
                    },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: "1px solid rgba(226,232,240,0.9)",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 3,
                            backgroundColor: "rgba(241,245,249,0.9)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#64748B",
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.4,
                              fontSize: 26,
                              fontWeight: 900,
                              color: "#0F172A",
                              letterSpacing: "-0.04em",
                            }}
                          >
                            {formatCount(item.value)}
                          </Typography>
                          <Typography
                            sx={{ mt: 0.25, fontSize: 12, color: "#64748B" }}
                          >
                            {item.helper}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </SectionCard>
            </Box>

            {/* HALF WIDTH - Quick Navigation */}
            <Box>
              <SectionCard
                title="Quick Navigation"
                subtitle="Move directly into the working areas for setup and execution."
              >
                <Stack spacing={1.25}>
                  {[
                    {
                      title: "Partners",
                      description:
                        "Manage EMS and contract manufacturing relationships.",
                      path: "/manufacturing/partners",
                    },
                    {
                      title: "Products",
                      description:
                        "Maintain manufacturable SKUs and revision baselines.",
                      path: "/manufacturing/products",
                    },
                    {
                      title: "Orders",
                      description:
                        "Release, allocate, and track active production runs.",
                      path: "/manufacturing/orders",
                    },
                  ].map((item) => (
                    <Button
                      key={item.title}
                      onClick={() => navigate(item.path)}
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        textTransform: "none",
                        p: 1.5,
                        borderRadius: 3,
                        border: "1px solid rgba(226,232,240,0.9)",
                        color: "#0F172A",
                        backgroundColor: "#fff",
                        "&:hover": {
                          backgroundColor: "#F8FAFC",
                          borderColor: "rgba(37,99,235,0.22)",
                        },
                      }}
                    >
                      <Box sx={{ textAlign: "left" }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.5,
                            fontSize: 12.5,
                            color: "#64748B",
                            lineHeight: 1.6,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                      <ArrowOutwardRoundedIcon
                        sx={{ fontSize: 18, mt: 0.25, color: "#1D4ED8" }}
                      />
                    </Button>
                  ))}
                </Stack>
              </SectionCard>
            </Box>
          </Box>
        </Stack>
      )}
    </PageShell>
  );
}

